import { useState, useEffect } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function InstallAppBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Check if already dismissed or installed
    const wasDismissed = sessionStorage.getItem("pwa-banner-dismissed");
    if (wasDismissed) return;

    // Check if already running as installed PWA
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const ua = navigator.userAgent;
    const iosDevice = /iphone|ipad|ipod/i.test(ua);
    setIsIOS(iosDevice);

    // Listen for Android/Chrome install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // For iOS, show manual guide after 3s on mobile
    if (iosDevice) {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      };
    }

    // For Chinese Android browsers (no beforeinstallprompt), show after 5s on mobile
    const isMobile = /android|mobile/i.test(ua);
    if (isMobile && !iosDevice) {
      const timer = setTimeout(() => setShowBanner(true), 5000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setInstalled(true);
        setTimeout(() => setShowBanner(false), 2000);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("pwa-banner-dismissed", "1");
    setTimeout(() => setShowBanner(false), 300);
  };

  if (isInstalled || !showBanner) return null;

  // iOS manual guide
  if (isIOS) {
    return (
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ${dismissed ? "translate-y-full" : "translate-y-0"}`}
      >
        <div className="bg-white border-t border-gray-200 px-4 py-4 shadow-lg">
          <div className="max-w-lg mx-auto">
            <div className="flex items-start gap-3">
              <img
                src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png"
                alt="鸿运商城"
                className="w-12 h-12 rounded-xl object-contain flex-shrink-0 border border-gray-100"
              />
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 font-semibold text-sm mb-0.5">安装鸿运商城 App</p>
                <p className="text-gray-500 text-xs leading-relaxed">
                  点击底部
                  <span className="inline-flex items-center mx-1 px-1.5 py-0.5 bg-gray-100 rounded text-gray-700 font-medium">
                    <i className="ri-share-forward-line mr-0.5 text-xs"></i>分享
                  </span>
                  按钮，然后选择
                  <span className="inline-flex items-center mx-1 px-1.5 py-0.5 bg-gray-100 rounded text-gray-700 font-medium">
                    <i className="ri-add-box-line mr-0.5 text-xs"></i>添加到主屏幕
                  </span>
                  即可安装
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0 -mt-0.5"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            {/* Arrow pointing down to Safari share button */}
            <div className="flex justify-center mt-3">
              <div className="flex items-center gap-1.5 text-red-500 text-xs font-medium animate-bounce">
                <i className="ri-arrow-down-line"></i>
                <span>点击下方分享按钮</span>
                <i className="ri-arrow-down-line"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android / Chrome / Chinese browsers
  return (
    <div
      className={`fixed bottom-4 left-4 right-4 z-50 transition-all duration-300 ${dismissed ? "opacity-0 translate-y-4 pointer-events-none" : "opacity-100 translate-y-0"}`}
    >
      <div className="max-w-sm mx-auto bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-md">
        {/* Red accent top bar */}
        <div className="h-1 bg-gradient-to-r from-red-500 to-orange-400"></div>
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <img
              src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png"
              alt="鸿运商城"
              className="w-12 h-12 rounded-xl object-contain border border-gray-100 flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 font-bold text-sm">皇冠鸿运商城</p>
              <p className="text-gray-400 text-xs">安装到主屏幕，随时购物更方便</p>
            </div>
            <button
              onClick={handleDismiss}
              className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Feature highlights */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {[
              { icon: "ri-flashlight-line", text: "极速启动" },
              { icon: "ri-wifi-off-line", text: "离线可用" },
              { icon: "ri-notification-3-line", text: "消息推送" },
            ].map((f) => (
              <div key={f.text} className="flex items-center gap-1 text-gray-500 text-xs">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${f.icon} text-red-400 text-xs`}></i>
                </div>
                <span>{f.text}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleDismiss}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              稍后再说
            </button>
            {deferredPrompt ? (
              <button
                onClick={handleInstall}
                className={`flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  installed ? "bg-green-500" : "bg-red-500 hover:bg-red-600"
                }`}
              >
                {installed ? (
                  <><i className="ri-check-line mr-1"></i>安装成功！</>
                ) : (
                  <><i className="ri-download-line mr-1"></i>立即安装</>
                )}
              </button>
            ) : (
              <button
                onClick={handleDismiss}
                className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-1"></i>添加到主屏幕
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
